import { selectChatMessages } from '@/redux/selectors/aiLegalMessageSelectors';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getLanguageDirection } from '../../../i18n/config';
import { AILegalMessage } from '../../../models/ai-legal-message';
import { selectHasActiveProgressForChat } from '../../../redux/slices/simpleLegalProgressSlice';
import { useAppSelector } from '../../../redux/store';
import Message from '../../message/message';
import EmptyChat from './EmptyChat';
import WelcomeMessage from './WelcomeMessage';
import MessageListLoading from './MessageListLoading';
import { selectCurrentChat } from '../../../redux/selectors/chatSelectors';
import DocumentSearchView from '../../document-search-results/DocumentSearchView';

interface MessageListProps {
  loadMoreTriggerRef?: React.RefObject<HTMLDivElement>;
  hasMoreMessages?: boolean;
  isLoadingMore?: boolean;
}

const MessageList: React.FC<MessageListProps> = ({
  loadMoreTriggerRef,
  hasMoreMessages = false,
  isLoadingMore = false
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();

  const currentChatId = useAppSelector((state) => state.ChatReducer.currentChatId);
  const isChatLoading = useAppSelector((state) => state.ChatReducer.isChatLoading);
  const currentChat = useAppSelector(selectCurrentChat);
  const messages = useAppSelector(selectChatMessages) as AILegalMessage[];

  const hasActiveProgress = useAppSelector(state =>
    currentChatId ? selectHasActiveProgressForChat(state, currentChatId) : false
  );

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'auto',
      block: 'end'
    });
  };

  // Must be called before any conditional returns
  useEffect(() => {
    if (hasActiveProgress) {
      scrollToBottom();
    }
  }, [hasActiveProgress]);

  if (currentChat?.type === 'documentSearch') {
    return <DocumentSearchView />;
  }

  if (isChatLoading) {
    return <MessageListLoading />;
  }

  if (!currentChatId) {
    return <WelcomeMessage />;
  }

  if (!messages || messages.length === 0) {
    return <EmptyChat />;
  }

  return (
    <div dir={getLanguageDirection(i18n.language)} className="px-1 text-start">
      {/* Lazy-load spinner at top */}
      {hasMoreMessages && messages.length >= 2 && isLoadingMore && (
        <div className="flex justify-center py-2">
          <Loader2 size={16} className="animate-spin text-gold" />
        </div>
      )}

      {messages.map((message, index) => {
        const is2ndFromTop = index === 1;
        return (
          <div key={message._id} ref={is2ndFromTop ? loadMoreTriggerRef : undefined}>
            <Message messageId={message._id} />
          </div>
        );
      })}

      {/* Fallback loading indicator (while isChatLoading but no active progress) */}
      {isChatLoading && !hasActiveProgress && (
        <div className="mt-2 mx-2">
          <div className="flex items-center ml-2 gap-2">
            <Loader2 size={20} className="animate-spin text-gold" />
            <p className="text-sm text-muted-foreground">{t('thinking')}</p>
          </div>
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;
